import { useEffect, useState } from 'react';

// Extract phone normalization function from the route
function normalizeBelgianPhoneNumber(phoneNumber: string): string {
  if (!phoneNumber.startsWith('+32')) {
    // Return original if it's not a standard Belgian mobile to be normalized
    return phoneNumber;
  }
  // Get the part after the country code and remove all non-digits
  const numberPart = phoneNumber.substring(3).replace(/\D/g, '');
  // Find the start of the mobile number part (which begins with '4')
  const mobileStartIndex = numberPart.indexOf('4');
  if (mobileStartIndex !== -1) {
    const mobileCandidate = numberPart.substring(mobileStartIndex);
    // A Belgian mobile number has 9 digits (e.g., 4xx xxx xxx)
    if (mobileCandidate.length === 9) {
      return `+32${mobileCandidate}`;
    }
  }
  // If the number doesn't match the expected mobile format, return original to avoid data loss.
  return phoneNumber;
}

export const useUserLeadsByPhone = (phoneNumber: string | null, agencyId: string | null, submissionCount: number = 0) => {
  const [leadCount, setLeadCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUserAndLeads = async () => {
      if (!phoneNumber || !agencyId) {
        // Don't reset leadCount if we're showing an error (submissionCount = -1)
        if (submissionCount !== -1) {
          setLeadCount(0);
        }
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Normalize the phone number
        const normalizedPhone = normalizeBelgianPhoneNumber(phoneNumber);

        // First, find the user by phone number and agencyId
        const userResponse = await fetch(`/api/users/search?phone=${encodeURIComponent(normalizedPhone)}&agencyId=${encodeURIComponent(agencyId)}`);
        
        if (!userResponse.ok) {
          // If user not found, leadCount remains 0
          setLeadCount(0);
          setLoading(false);
          return;
        }

        const userData = await userResponse.json();
        
        if (!userData.user) {
          // No user found, leadCount remains 0
          setLeadCount(0);
          setLoading(false);
          return;
        }

        // Then, fetch all leads for this user
        const leadsResponse = await fetch(`/api/leads?guestId=${userData.user.userId}`);
        
        if (!leadsResponse.ok) {
          // If leads fetch fails, assume 0 leads
          setLeadCount(0);
          setLoading(false);
          return;
        }

        const leadsData = await leadsResponse.json();
        
        const leads = leadsData.leads || [];
        setLeadCount(leads.length);
        setLoading(false);

      } catch (err: any) {
        // On error, assume 0 leads
        setLeadCount(0);
        setLoading(false);
      }
    };

    fetchUserAndLeads();
  }, [phoneNumber, agencyId, submissionCount]); // Add submissionCount to dependencies

  return {
    leadCount,
    loading,
  };
}; 